import { useQuery, UseQueryOptions, useSuspenseQuery } from '@tanstack/react-query';
import {
    useGetConstructionProjectById,
    useGetConstructionProjectByNumberClient,
    useGetConstructionProjectListByOrganizationIdAndStatusIn,
} from './ConstructionProject.ts';
import {
    FilterOptionsConstructionProject,
    GetConstructionProjectResponse,
} from '@/clients/constructionProject/types/constructionProject.ts';
import { getStaleTimeInHours } from '@/shared/utils/staleTime.ts';
import { ProjectNumber } from '@schuettflix/interfaces';

export function getUseConstructionProjectQueryKey(id: string | null | undefined) {
    return ['constructionProject', id];
}

export function getUseConstructionProjectByNumberQueryKey(projectNumber: ProjectNumber) {
    return ['constructionProjectByNumber', projectNumber];
}

const STALE_TIME = getStaleTimeInHours(1);

export function useConstructionProject(
    id: string | null | undefined,
    options?: UseQueryOptions<GetConstructionProjectResponse>
) {
    const { getConstructionProjectById } = useGetConstructionProjectById();

    return useQuery({
        queryKey: getUseConstructionProjectQueryKey(id),
        queryFn: () => getConstructionProjectById(id!),
        enabled: !!id,
        refetchOnWindowFocus: false,
        staleTime: STALE_TIME,
        ...options,
    });
}

export function useSuspenseConstructionProject(id: string) {
    const { getConstructionProjectById } = useGetConstructionProjectById();

    return useSuspenseQuery({
        queryKey: getUseConstructionProjectQueryKey(id),
        queryFn: () => getConstructionProjectById(id),
        refetchOnWindowFocus: false,
        staleTime: STALE_TIME,
    });
}

export function useConstructionProjectSuspenseQuery(id: string | null | undefined) {
    const { getConstructionProjectById } = useGetConstructionProjectById();

    return useSuspenseQuery({
        queryKey: getUseConstructionProjectQueryKey(id),
        queryFn: () => (id ? getConstructionProjectById(id) : null),
        refetchOnWindowFocus: false,
        staleTime: STALE_TIME,
    });
}

export function useConstructionProjectByNumber(projectNumber: ProjectNumber) {
    const { getConstructionProjectProjectNumber } = useGetConstructionProjectByNumberClient();

    return useSuspenseQuery({
        queryKey: getUseConstructionProjectByNumberQueryKey(projectNumber),
        queryFn: () => getConstructionProjectProjectNumber(projectNumber),
        refetchOnWindowFocus: false,
        staleTime: STALE_TIME,
    });
}

export function useFilteredConstructionProjects(
    organizationId: number | null,
    filterOptions: FilterOptionsConstructionProject
) {
    const { getConstructionProjectListByOrganizationIdAndStatusIn } =
        useGetConstructionProjectListByOrganizationIdAndStatusIn();

    return useQuery({
        queryKey: ['constructionProjects', organizationId, filterOptions],
        queryFn: () => {
            if (!organizationId) {
                return Promise.resolve({ constructionProjects: [] });
            }
            return getConstructionProjectListByOrganizationIdAndStatusIn(organizationId, filterOptions);
        },
        enabled: !!organizationId,
        refetchOnWindowFocus: false,
        staleTime: STALE_TIME,
    });
}
